import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CalendarCheck } from 'lucide-react';
import api from '../api/axios';

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [
      { freq: 587.33, start: 0, dur: 0.15 },
      { freq: 783.99, start: 0.15, dur: 0.15 },
      { freq: 987.77, start: 0.3, dur: 0.3 },
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    });
  } catch {}
};

const NotificationAlert = () => {
  const [notifications, setNotifications] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [latestReservation, setLatestReservation] = useState(null);
  const lastCountRef = useRef(0);
  const initialLoadRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread');
      const newNotifs = res.data;
      if (!initialLoadRef.current && newNotifs.length > lastCountRef.current && newNotifs.length > 0) {
        playNotificationSound();
        const newest = newNotifs[0];
        if (newest?.type === 'reservation') {
          setLatestReservation(newest);
          setShowModal(true);
        }
      }
      initialLoadRef.current = false;
      lastCountRef.current = newNotifs.length;
      setNotifications(newNotifs);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try { await api.put(`/notifications/${id}/read`); } catch {}
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (notifications.length <= 1) setShowPopup(false);
  };

  const markAllRead = async () => {
    try { await api.put('/notifications/read-all'); } catch {}
    setNotifications([]);
    setShowPopup(false);
    setShowModal(false);
    lastCountRef.current = 0;
  };

  const hasNotifs = notifications.length > 0;

  return (
    <>
      {/* Floating bell button */}
      <button
        onClick={() => setShowPopup(!showPopup)}
        data-testid="notification-bell"
        className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
          hasNotifs
            ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-300/50'
            : 'bg-white hover:bg-stone-50 border border-stone-200'
        }`}
      >
        <Bell size={20} className={hasNotifs ? 'text-white animate-bounce' : 'text-stone-400'} />
        {hasNotifs && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1 shadow">
            {notifications.length}
          </span>
        )}
      </button>

      {/* Popup list - opens upward from bottom-right */}
      {showPopup && hasNotifs && (
        <div className="absolute bottom-14 right-0 w-80 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden" data-testid="notification-popup">
          <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-100">
            <span className="text-sm font-bold text-amber-800">Notifications ({notifications.length})</span>
            <button onClick={markAllRead} className="text-[10px] font-semibold text-amber-600 hover:text-amber-800 uppercase tracking-wider" data-testid="mark-all-read">
              Tout lire
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {notifications.map(n => (
              <div key={n.id} className="flex items-start gap-3 px-4 py-3 border-b border-stone-100 hover:bg-stone-50" data-testid={`notif-${n.id}`}>
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CalendarCheck size={14} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-stone-800">{n.title}</p>
                  <p className="text-[11px] text-stone-600 mt-0.5">{n.message}</p>
                </div>
                <button onClick={() => markAsRead(n.id)} className="p-1 rounded hover:bg-green-100 flex-shrink-0" data-testid={`notif-read-${n.id}`}>
                  <Check size={14} className="text-green-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full-screen modal alert for new reservation */}
      {showModal && latestReservation && (
        <div className="fixed inset-0 bg-black/40 z-[10000] flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-in zoom-in-95" onClick={e => e.stopPropagation()} data-testid="reservation-alert-modal">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarCheck size={28} className="text-amber-600 animate-bounce" />
            </div>
            <h3 className="text-lg font-bold text-stone-800 mb-2">Nouvelle Reservation !</h3>
            <p className="text-sm text-stone-600 mb-4">{latestReservation.message}</p>
            <div className="flex gap-2">
              <button
                onClick={() => { markAsRead(latestReservation.id); setShowModal(false); }}
                className="flex-1 bg-amber-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-amber-700 transition-colors"
                data-testid="alert-confirm"
              >
                Compris
              </button>
              <button
                onClick={markAllRead}
                className="flex-1 border border-stone-300 text-stone-600 py-2.5 rounded-lg text-sm font-bold hover:bg-stone-50 transition-colors"
                data-testid="alert-dismiss-all"
              >
                Tout lire
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationAlert;
